/**
 * NOT CURRENTLY USED.
 *
 * The teacher portal navigation is attendance-only (see NAV_ITEMS in StaffLayout), so
 * this page is unreachable from the UI. Its route is still registered in StaffApp, so
 * direct links resolve and re-enabling it only takes restoring its nav entry.
 *
 * It has not been carried through the mobile-first attendance rework: it is desktop-
 * oriented and predates the school/grade roster segmentation. Left as-is on purpose —
 * do not treat it as a current example of how these screens should look.
 */
import { useState, useMemo, useEffect } from 'react';
import { LocationPill } from '../../components/LocationPill';
import { PROGRAM_LOCATION_EMBED, programLocationName, type WithProgramLocation } from '../../types/location';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { StaffLayout } from '../StaffLayout';
import { ScheduleGrid } from '../../components/ScheduleGrid';
import { ScheduleEventList } from '../../components/ScheduleEventList';
import { normalizeScheduleGrid, type ScheduleGridData } from '../../types/schedule';
import { Printer } from 'lucide-react';
import { formatDateDisplay } from '../../utils/date';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import { getScheduleDescription } from '../../types/program';
import { getTodaySessionDate } from '../../utils/schedule';
import { NON_TEACHING_TYPES_FILTER } from '../programSessions';

interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  program_ids: string[] | null;
}

interface TeachingProgram extends WithProgramLocation {
  id: string;
  name: string;
  type: string;
  billing_type: string;
  schedule_cron: string;
  start_date: string;
  end_date: string;
}

interface ScheduleRowResult {
  theme_en: string | null;
  theme_zh: string | null;
  notes: string | null;
  schedule: ScheduleGridData;
}

interface RosterStudent {
  id: string;
  first_name: string;
  last_name: string;
  grade: string | null;
  viaSummerPass: boolean;
  hasAllergies: boolean;
  allergiesDetails: string | null;
  photoRelease: boolean | null;
  lunchOpted: boolean;
  paymentStatus: string | null;
  selectedDays: string[] | null;
}

/** Row shape returned by the staff_camp_roster RPC. */
interface RosterRpcRow {
  student_id: string;
  first_name: string;
  last_name: string;
  grade: string | null;
  via_summer_pass: boolean;
  has_allergies: boolean;
  allergies_details: string | null;
  photo_release: boolean | null;
  lunch_opted: boolean;
  payment_status: string | null;
  selected_days: string[] | null;
}

function shortDate(dateStr: string): string {
  const [, mm, dd] = dateStr.split('-');
  return `${parseInt(mm)}/${parseInt(dd)}`;
}

// Statuses that mean "payment still needs attention" — everything else
// (paid, processing, voided, or no invoice at all) reads as confirmed.
const PENDING_PAYMENT_STATUSES = new Set(['pending', 'partial', 'overdue']);

function isPendingPayment(status: string | null): boolean {
  return status != null && PENDING_PAYMENT_STATUSES.has(status);
}

function PaymentBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-gray-400">—</span>;
  return isPendingPayment(status)
    ? <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">🚫 Pending</span>
    : <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Confirmed</span>;
}

/** Summer Pass + allergy badges shown next to a student's name. */
function StudentBadges({ s }: { s: RosterStudent }) {
  return (
    <>
      {s.viaSummerPass && (
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 whitespace-nowrap">Summer Pass</span>
      )}
      {s.hasAllergies && (
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
          ⚠ Allergy{s.allergiesDetails ? `: ${s.allergiesDetails}` : ''}
        </span>
      )}
    </>
  );
}

interface Props {
  staffUser: StaffUser;
}

type ScheduleTab = 'roster' | 'calendar' | 'events';
const SCHEDULE_TABS: readonly ScheduleTab[] = ['roster', 'calendar', 'events'];

export function StaffSchedule({ staffUser }: Props) {
  const scopedIds = staffUser.program_ids;
  const [selectedId, setSelectedId] = useState<string>('');
  const [tab, setTab] = useUrlTabState<ScheduleTab>('tab', SCHEDULE_TABS, 'roster');

  const { data: programs = [], isLoading: programsLoading } = useQuery<TeachingProgram[]>({
    queryKey: ['staff-schedule-programs', scopedIds],
    queryFn: async () => {
      let query = supabase
        .from('programs')
        .select(`id, name, type, billing_type, schedule_cron, start_date, end_date, ${PROGRAM_LOCATION_EMBED}`)
        .eq('active', true)
        .not('type', 'in', NON_TEACHING_TYPES_FILTER);
      if (scopedIds) query = query.in('id', scopedIds);
      const { data, error } = await query.order('start_date');
      if (error) throw error;
      return (data ?? []) as TeachingProgram[];
    },
  });

  // Default to a currently-running program, else the next upcoming, else the first.
  useEffect(() => {
    if (selectedId || programs.length === 0) return;
    const today = getTodaySessionDate();
    const current = programs.find(p => p.start_date <= today && p.end_date >= today);
    const upcoming = programs.find(p => p.start_date >= today);
    setSelectedId((current ?? upcoming ?? programs[0]).id);
  }, [programs, selectedId]);

  const selectedProgram = useMemo(
    () => programs.find(p => p.id === selectedId) ?? null,
    [programs, selectedId],
  );

  const { data: schedule, isLoading: scheduleLoading } = useQuery<ScheduleRowResult | null>({
    queryKey: ['staff-schedule', selectedId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('program_schedules')
        .select('theme_en, theme_zh, notes, schedule')
        .eq('program_id', selectedId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as unknown as ScheduleRowResult;
      return { ...row, schedule: normalizeScheduleGrid(row.schedule) };
    },
    enabled: Boolean(selectedId),
  });

  const { data: roster = [], isLoading: rosterLoading } = useQuery<RosterStudent[]>({
    queryKey: ['staff-schedule-roster', selectedId],
    queryFn: async () => {
      // Enriched roster via SECURITY DEFINER RPC: includes Full Summer Pass
      // students, lunch opt-in, allergy/photo-release, and payment status —
      // already deduped and sorted by (last_name, first_name).
      const { data, error } = await supabase.rpc('staff_camp_roster', { p_program_id: selectedId });
      if (error) throw error;
      return ((data ?? []) as RosterRpcRow[]).map(r => ({
        id: r.student_id,
        first_name: r.first_name,
        last_name: r.last_name,
        grade: r.grade,
        viaSummerPass: r.via_summer_pass,
        hasAllergies: r.has_allergies,
        allergiesDetails: r.allergies_details,
        photoRelease: r.photo_release,
        lunchOpted: r.lunch_opted,
        paymentStatus: r.payment_status,
        selectedDays: r.selected_days,
      }));
    },
    enabled: Boolean(selectedId),
  });

  const isPerDay = selectedProgram?.billing_type === 'per_day';

  // The activity timetable is authored per program and most regular programs don't have
  // one. Only offer the Calendar / Event List tabs when there is something to show.
  const hasTimetable = (schedule?.schedule.days.length ?? 0) > 0;

  // Keep the tab selection valid when switching to a program with no timetable —
  // this also self-heals a shared `?tab=calendar` link pointed at such a program.
  useEffect(() => {
    if (!hasTimetable && tab !== 'roster') setTab('roster');
  }, [hasTimetable, tab, setTab]);

  const tabs = useMemo<ReadonlyArray<readonly [typeof tab, string]>>(
    () => hasTimetable
      ? [['roster', 'Roster'], ['calendar', 'Calendar'], ['events', 'Event List']]
      : [['roster', 'Roster']],
    [hasTimetable],
  );

  // Per-day programs: sorted unique dates + student-count per date.
  const perDayCounts = useMemo<Array<{ date: string; count: number }>>(() => {
    if (!isPerDay || roster.length === 0) return [];
    const map: Record<string, number> = {};
    for (const s of roster) {
      for (const d of s.selectedDays ?? []) {
        map[d] = (map[d] ?? 0) + 1;
      }
    }
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({ date, count }));
  }, [isPerDay, roster]);

  // Quick at-a-glance counts for the roster header — saves teachers from
  // tallying lunch/allergy/pass counts by hand on the day.
  const rosterSummary = useMemo(() => ({
    total: roster.length,
    lunch: roster.filter(s => s.lunchOpted).length,
    allergy: roster.filter(s => s.hasAllergies).length,
    summerPass: roster.filter(s => s.viaSummerPass).length,
    noPhoto: roster.filter(s => s.photoRelease === false).length,
    pending: roster.filter(s => isPendingPayment(s.paymentStatus)).length,
  }), [roster]);

  // Lunch is a camp-era enrollment add-on; regular programs never carry it, so the column
  // and chip only appear when at least one student on this roster actually opted in.
  const hasLunch = rosterSummary.lunch > 0;

  // Pending-payment students sink to the bottom so confirmed students are
  // listed first; order is otherwise unchanged (stable sort).
  const sortedRoster = useMemo(
    () => [...roster].sort((a, b) => Number(isPendingPayment(a.paymentStatus)) - Number(isPendingPayment(b.paymentStatus))),
    [roster],
  );

  return (
    <StaffLayout staffUser={staffUser}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Programs</h1>
        {programs.length > 1 && (
          <select
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            className="w-full sm:w-auto sm:max-w-lg px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      {programsLoading ? (
        <div className="space-y-3">
          <div className="h-6 w-48 bg-gray-100 animate-pulse rounded" />
          <div className="h-64 bg-gray-100 animate-pulse rounded-xl" />
        </div>
      ) : programs.length === 0 ? (
        <p className="text-gray-500 text-sm">No programs assigned yet.</p>
      ) : (
        <>
          {/* Program header */}
          <div className="mb-4">
            {/* Program name is shown in the dropdown when there's a choice; only repeat it here when there isn't one. */}
            {programs.length <= 1 && (
              <p className="text-lg font-semibold text-gray-900">{selectedProgram?.name}</p>
            )}
            {(schedule?.theme_en || schedule?.theme_zh) && (
              <p className="text-sm text-gray-600 mt-0.5">
                {schedule?.theme_en}
                {schedule?.theme_en && schedule?.theme_zh ? ' · ' : ''}
                {schedule?.theme_zh}
              </p>
            )}
            {selectedProgram && (
              <p className="text-sm text-gray-600 mt-0.5">
                {getScheduleDescription(selectedProgram.schedule_cron)}
              </p>
            )}
            {selectedProgram && (
              <p className="text-xs text-gray-400 mt-0.5">
                {formatDateDisplay(selectedProgram.start_date)} – {formatDateDisplay(selectedProgram.end_date)}
              </p>
            )}
            <LocationPill name={programLocationName(selectedProgram)} size="sm" className="mt-1" />
            {schedule?.notes && (
              <p className="text-xs text-gray-400 mt-0.5">{schedule.notes}</p>
            )}
          </div>

          {/* Sub-tabs */}
          <div className="border-b border-gray-200 mb-4 flex items-center gap-4">
            {tabs.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`-mb-px border-b-2 px-1 py-2 text-sm font-medium transition-colors ${
                  tab === key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}{key === 'roster' && !rosterLoading ? ` (${roster.length})` : ''}
              </button>
            ))}
            {tab === 'roster' && !rosterLoading && roster.length > 0 && (
              <button
                onClick={() => window.print()}
                className="ml-auto mb-0.5 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors print:hidden"
                title="Print roster"
              >
                <Printer className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Calendar tab */}
          {tab === 'calendar' && (
            scheduleLoading
              ? <div className="h-64 bg-gray-100 animate-pulse rounded-xl" />
              : <ScheduleGrid data={schedule?.schedule ?? { days: [] }} />
          )}

          {/* Event list tab */}
          {tab === 'events' && (
            scheduleLoading
              ? <div className="h-64 bg-gray-100 animate-pulse rounded-xl" />
              : <ScheduleEventList data={schedule?.schedule ?? { days: [] }} />
          )}

          {/* Roster tab */}
          {tab === 'roster' && (
            rosterLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-12 bg-gray-100 animate-pulse rounded-lg" />)}
            </div>
          ) : roster.length === 0 ? (
            <p className="text-gray-500 text-sm">No active enrollments.</p>
          ) : (
            <>
            {/* At-a-glance counts */}
            <div className="mb-3 space-y-2">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                <span><span className="font-semibold text-gray-900">{rosterSummary.total}</span> students</span>
                {rosterSummary.lunch > 0 && (
                  <span><span className="font-semibold text-yellow-600">{rosterSummary.lunch}</span> 🍱 lunch</span>
                )}
                <span><span className="font-semibold text-red-700">{rosterSummary.allergy}</span> {rosterSummary.allergy === 1 ? 'allergy' : 'allergies'}</span>
                {rosterSummary.summerPass > 0 && (
                  <span><span className="font-semibold text-indigo-700">{rosterSummary.summerPass}</span> Summer Pass</span>
                )}
                {rosterSummary.noPhoto > 0 && (
                  <span><span className="font-semibold text-red-700">{rosterSummary.noPhoto}</span> no photo release</span>
                )}
                <span className="text-gray-300 select-none">|</span>
                <span><span className="font-semibold text-green-700">{rosterSummary.total - rosterSummary.pending}</span> confirmed</span>
                {rosterSummary.pending > 0 && (
                  <span><span className="font-semibold text-amber-600">{rosterSummary.pending}</span> pending</span>
                )}
              </div>
              {perDayCounts.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {perDayCounts.map(({ date, count }) => (
                    <span key={date} className="inline-flex items-baseline gap-1 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-xs">
                      <span className="font-medium text-indigo-700">{shortDate(date)}</span>
                      <span className="text-indigo-400">·</span>
                      <span className="text-indigo-600">{count} students</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {/* Desktop table */}
              <table className="min-w-full text-sm hidden lg:table">
                <thead className="bg-gray-50">
                  <tr>
                    {['Student', 'Grade', ...(isPerDay ? ['Days'] : []), ...(hasLunch ? ['Lunch'] : []), 'Photo', 'Status'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedRoster.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-gray-900">{s.first_name} {s.last_name}</span>
                          <StudentBadges s={s} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{s.grade ?? '—'}</td>
                      {isPerDay && (
                        <td className="px-4 py-3">
                          {(s.selectedDays ?? []).length > 0
                            ? <div className="flex flex-wrap gap-1">
                                {(s.selectedDays ?? []).map(d => (
                                  <span key={d} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100">
                                    {shortDate(d)}
                                  </span>
                                ))}
                              </div>
                            : <span className="text-gray-400">—</span>
                          }
                        </td>
                      )}
                      {hasLunch && (
                        <td className="px-4 py-3">
                          {s.lunchOpted
                            ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 whitespace-nowrap">🍱 Lunch</span>
                            : <span className="text-gray-400">—</span>}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        {s.photoRelease === false
                          ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 whitespace-nowrap">No photo</span>
                          : s.photoRelease === true
                            ? <span className="text-xs text-gray-500">Yes</span>
                            : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3"><PaymentBadge status={s.paymentStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile cards */}
              <div className="lg:hidden divide-y divide-gray-100">
                {sortedRoster.map(s => (
                  <div key={s.id} className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="font-semibold text-gray-900">{s.first_name} {s.last_name}</p>
                      {s.grade && (
                        <span className="flex-shrink-0 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{s.grade}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                      <StudentBadges s={s} />
                      {isPerDay && (s.selectedDays ?? []).length > 0 && (s.selectedDays ?? []).map(d => (
                        <span key={d} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100">
                          {shortDate(d)}
                        </span>
                      ))}
                      {s.lunchOpted && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700">🍱 Lunch</span>
                      )}
                      {s.photoRelease === false && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">No photo</span>
                      )}
                      <PaymentBadge status={s.paymentStatus} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </>
          ))}
        </>
      )}

      {/* Print-only roster table — hidden on screen, shown by @media print */}
      {roster.length > 0 && (
        <div className="printable-roster">
          <div style={{ marginBottom: '12px' }}>
            <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>{selectedProgram?.name ?? 'Roster'}</h1>
            <p style={{ fontSize: '13px', color: '#555', margin: '2px 0 0' }}>
              {selectedProgram ? `${formatDateDisplay(selectedProgram.start_date)} – ${formatDateDisplay(selectedProgram.end_date)}` : ''}
              {' · '}Printed {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
            <p style={{ fontSize: '12px', color: '#555', margin: '2px 0 0' }}>
              {rosterSummary.total} students
              {rosterSummary.lunch > 0 && ` · ${rosterSummary.lunch} lunch`}
              {rosterSummary.allergy > 0 && ` · ${rosterSummary.allergy} ${rosterSummary.allergy === 1 ? 'allergy' : 'allergies'}`}
              {rosterSummary.summerPass > 0 && ` · ${rosterSummary.summerPass} Summer Pass`}
              {rosterSummary.noPhoto > 0 && ` · ${rosterSummary.noPhoto} no photo`}
            </p>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #000' }}>
                {['#', 'Student', 'Grade', ...(isPerDay ? ['Days'] : []), 'Allergy', ...(hasLunch ? ['Lunch'] : []), 'Photo', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '4px 8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRoster.map((s, idx) => (
                <tr key={s.id} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '5px 8px', verticalAlign: 'top' }}>{idx + 1}</td>
                  <td style={{ padding: '5px 8px', verticalAlign: 'top', fontWeight: 600 }}>
                    {s.first_name} {s.last_name}{s.viaSummerPass ? ' ★' : ''}
                  </td>
                  <td style={{ padding: '5px 8px', verticalAlign: 'top' }}>{s.grade ?? '—'}</td>
                  {isPerDay && (
                    <td style={{ padding: '5px 8px', verticalAlign: 'top' }}>
                      {(s.selectedDays ?? []).map(d => shortDate(d)).join(', ') || '—'}
                    </td>
                  )}
                  <td style={{ padding: '5px 8px', verticalAlign: 'top' }}>
                    {s.hasAllergies ? `⚠ ${s.allergiesDetails ?? 'Yes'}` : '—'}
                  </td>
                  {hasLunch && (
                    <td style={{ padding: '5px 8px', verticalAlign: 'top' }}>{s.lunchOpted ? '✓' : '—'}</td>
                  )}
                  <td style={{ padding: '5px 8px', verticalAlign: 'top' }}>
                    {s.photoRelease === false ? 'No' : s.photoRelease === true ? 'Yes' : '—'}
                  </td>
                  <td style={{ padding: '5px 8px', verticalAlign: 'top' }}>
                    {s.paymentStatus ? (isPendingPayment(s.paymentStatus) ? '🚫 Pending' : '✓ Confirmed') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {roster.some(s => s.viaSummerPass) && (
            <p style={{ fontSize: '11px', color: '#777', marginTop: '8px' }}>★ Full Summer Pass student</p>
          )}
        </div>
      )}
    </StaffLayout>
  );
}
